import { useEffect, useState } from "react";
import { Outlet, NavLink, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { NAV_GROUPS } from "@/lib/reportsConfig";
import { toast } from "sonner";
import AIDrawer from "@/components/AIDrawer";
import { LogOut, Database, RefreshCw, Loader2, ChevronDown, ChevronRight } from "lucide-react";

export default function Layout() {
  const { user, logout } = useAuth();
  const [status, setStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [collapsed, setCollapsed] = useState({});
  const [pending, setPending] = useState(0);
  const location = useLocation();

  useEffect(() => {
    api.get("/system/status").then(({ data }) => setStatus(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user?.role !== "admin") return;
    const fetchPending = () => api.get("/users/pending-count").then(({ data }) => setPending(data.count)).catch(() => {});
    fetchPending();
    const t = setInterval(fetchPending, 30000);
    return () => clearInterval(t);
  }, [user, location.pathname]);

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/system/sync");
      toast.info(data.message || "Extraction started…");
      const t0 = Date.now();
      while (Date.now() - t0 < 15 * 60 * 1000) {
        await new Promise((r) => setTimeout(r, 4000));
        const { data: job } = await api.get("/system/sync/status");
        if (job.status === "done") {
          setStatus((s) => ({ ...s, last_extraction: job.last_extraction || job.finished }));
          toast.success(job.message || "Extraction finished");
          return;
        }
        if (job.status === "failed") {
          toast.error(job.message || "Extraction failed");
          return;
        }
      }
      toast.error("Extraction is taking unusually long — check the Extraction Log in Settings later");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-[#F6F7F9] text-slate-800">
      <aside className="w-64 flex-shrink-0 border-r border-white/10 bg-[#14202E] flex flex-col fixed inset-y-0 z-30" data-testid="sidebar">
        <div className="h-16 flex items-center gap-3 px-5 border-b border-white/10">
          <img src="/dee-logo.png" alt="Dee Piping" className="h-8 w-auto object-contain" data-testid="sidebar-logo" />
          <div className="leading-tight">
            <div className="font-bold text-sm tracking-tight text-white" style={{ fontFamily: "Outfit" }}>DEE PIPING</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400">Analytics Platform</div>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto thin-scroll py-4 px-3 space-y-5">
          {NAV_GROUPS.map((group) => {
            const items = group.items.filter((i) => {
              if (i.adminOnly) return user?.role === "admin";
              if (user?.role === "admin") return true;
              return i.perm ? (user?.permissions || []).includes(i.perm) : true;
            });
            if (!items.length) return null;
            const isCollapsed = collapsed[group.label];
            const groupHasPending = pending > 0 && items.some((i) => i.to === "/users");
            return (
              <div key={group.label}>
                <button
                  onClick={() => setCollapsed((c) => ({ ...c, [group.label]: !c[group.label] }))}
                  data-testid={`nav-group-${group.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="w-full flex items-center justify-between px-2 mb-1.5 text-[10px] font-semibold uppercase tracking-widest text-slate-500 hover:text-slate-300 transition-colors"
                >
                  <span className="flex items-center gap-1.5">
                    {group.label}
                    {isCollapsed && groupHasPending && <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />}
                  </span>
                  {isCollapsed ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
                </button>
                {!isCollapsed && (
                <div className="space-y-0.5">
                  {items.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.end}
                      data-testid={`nav-${item.to.replace(/\//g, "-").replace(/^-/, "") || "dashboard"}`}
                      className={({ isActive }) =>
                        `flex items-center gap-2.5 px-2.5 py-2 rounded-md text-[13px] transition-colors ${
                          isActive
                            ? "bg-[#B01E24] text-white font-medium border border-transparent"
                            : "text-slate-400 hover:text-white hover:bg-white/[0.06] border border-transparent"
                        }`
                      }
                    >
                      <item.icon size={15} strokeWidth={1.8} />
                      <span className="flex-1">{item.label}</span>
                      {item.to === "/users" && pending > 0 && (
                        <span className="relative flex items-center" data-testid="pending-approval-indicator" title={`${pending} user(s) awaiting approval`}>
                          <span className="absolute inline-flex w-2.5 h-2.5 rounded-full bg-red-500 opacity-75 animate-ping" />
                          <span className="relative inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[9px] font-bold">
                            {pending}
                          </span>
                        </span>
                      )}
                    </NavLink>
                  ))}
                </div>
                )}
              </div>
            );
          })}
        </nav>
        <div className="p-3 border-t border-white/10">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="text-xs font-medium text-slate-200 truncate" data-testid="sidebar-user-name">{user?.name || user?.email}</div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500">{user?.role}</div>
            </div>
            <button
              onClick={logout}
              data-testid="logout-button"
              className="p-2 rounded-md text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-colors"
              title="Logout"
            >
              <LogOut size={15} />
            </button>
          </div>
        </div>
      </aside>

      <div className="flex-1 ml-64 flex flex-col min-w-0">
        <header className="h-16 border-b border-slate-200 bg-white/90 backdrop-blur sticky top-0 z-20 flex items-center justify-between px-6">
          <div className="flex items-center gap-3">
            <span
              data-testid="data-source-badge"
              className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full border ${
                status?.data_source === "SAP"
                  ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                  : "bg-amber-50 text-amber-700 border-amber-300"
              }`}
            >
              <Database size={11} />
              {status?.data_source === "SAP" ? "Live SAP Data" : "Mock SAP Data"}
            </span>
            {status?.last_extraction && (
              <span className="text-xs text-slate-500" data-testid="last-extraction">
                Last extraction: {new Date(status.last_extraction).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2.5">
            <button
              onClick={sync}
              disabled={syncing}
              data-testid="sync-sap-button"
              className="flex items-center gap-1.5 text-xs font-medium bg-white hover:bg-slate-50 disabled:opacity-60 text-slate-700 border border-slate-300 rounded-md px-3 py-1.5 transition-colors"
            >
              {syncing ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Sync SAP Data
            </button>
            <AIDrawer reportKey="dashboard" reportTitle="AI Analyst — Overall Position" buttonLabel="AI Analyst" />
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8" key={location.pathname}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
